import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Bookmark, SlidersHorizontal, X } from "lucide-react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { useToast } from "../../components/Toast";
import { mailbox, mailErrorMessage } from "../../api/mailbox";
import type { MailSavedSearch, MailSearchParams } from "../../api/types";

// 進階搜尋(規格 §2.6):日期區間、有無附件、附件檔名、寄件人、收件人、全部資料夾、星號、未讀、
// 含雲端已刪除;常用的可「存成捷徑」(`mail_saved_searches`,本人限定)。
//
// 捷徑只存條件;點捷徑時把條件原樣帶回 `GET /messages` 重播(後端兩邊共用同一個 `search.filtered`)。
// 這個元件只管「條件」與捷徑清單;關鍵字(`q`)與目前資料夾由頁面給,存捷徑時一起存進去。

/** 面板上的條件(不含關鍵字與資料夾)。 */
export interface SearchFilters {
  date_from?: string;
  date_to?: string;
  has_attachments?: boolean;
  attachment?: string;
  from?: string;
  to?: string;
  flagged?: boolean;
  unread?: boolean;
  /** 全部資料夾(`folder=*`)。 */
  all_folders?: boolean;
}

/** 有沒有任何條件(決定列表要不要切成「搜尋結果」)。 */
export function hasFilters(f: SearchFilters): boolean {
  return Boolean(
    f.date_from ||
      f.date_to ||
      f.has_attachments !== undefined ||
      f.attachment ||
      f.from ||
      f.to ||
      f.flagged ||
      f.unread ||
      f.all_folders,
  );
}

/** 條件 → 後端的搜尋參數。 */
export function toParams(f: SearchFilters, q: string, folder: string): MailSearchParams {
  const out: MailSearchParams = { folder: f.all_folders ? "*" : folder };
  if (q) out.q = q;
  if (f.date_from) out.date_from = f.date_from;
  if (f.date_to) out.date_to = f.date_to;
  if (f.has_attachments !== undefined) out.has_attachments = f.has_attachments;
  if (f.attachment) out.attachment = f.attachment;
  if (f.from) out.from = f.from;
  if (f.to) out.to = f.to;
  if (f.flagged) out.flagged = true;
  if (f.unread) out.unread = true;
  return out;
}

/** 捷徑的參數 → 面板條件 + 關鍵字 + 資料夾(`*` = 全部)。 */
export function fromParams(p: MailSearchParams): { filters: SearchFilters; q: string; folder: string | null } {
  const folder = p.folder && p.folder !== "*" ? p.folder : null;
  return {
    q: p.q ?? "",
    folder,
    filters: {
      date_from: p.date_from || undefined,
      date_to: p.date_to || undefined,
      has_attachments: typeof p.has_attachments === "boolean" ? p.has_attachments : undefined,
      attachment: p.attachment || undefined,
      from: p.from || undefined,
      to: p.to || undefined,
      flagged: p.flagged || undefined,
      unread: p.unread || undefined,
      all_folders: p.folder === "*" || !p.folder ? true : undefined,
    },
  };
}

export interface AdvancedSearchProps {
  /** 目前套用中的條件。 */
  value: SearchFilters;
  /** 關鍵字與目前資料夾(存捷徑用)。 */
  q: string;
  folder: string;
  onApply: (filters: SearchFilters) => void;
  /** 點了捷徑。 */
  onReplay: (params: MailSearchParams) => void;
}

export function AdvancedSearch({ value, q, folder, onApply, onReplay }: AdvancedSearchProps) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<SearchFilters>(value);
  const [saved, setSaved] = useState<MailSavedSearch[]>([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  // 外面換了條件(點了捷徑、清除):面板跟上
  useEffect(() => setDraft(value), [value]);

  useEffect(() => {
    let alive = true;
    mailbox
      .savedSearches()
      .then((rows) => {
        if (alive) setSaved(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const set = (patch: Partial<SearchFilters>) => setDraft((prev) => ({ ...prev, ...patch }));

  const apply = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onApply(draft);
  };

  const save = async () => {
    const clean = name.trim();
    if (!clean || saving) return;
    setSaving(true);
    try {
      const row = await mailbox.saveSearch(clean, toParams(draft, q, folder));
      setSaved((prev) => [...prev, row]);
      setName("");
      toast.push("已存成捷徑", "success");
    } catch (err) {
      toast.push(mailErrorMessage(err), "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: MailSavedSearch) => {
    try {
      await mailbox.deleteSavedSearch(row.id);
      setSaved((prev) => prev.filter((one) => one.id !== row.id));
    } catch (err) {
      toast.push(mailErrorMessage(err), "error");
    }
  };

  const attachmentChoice = draft.has_attachments === undefined ? "" : draft.has_attachments ? "yes" : "no";

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="ghost"
          aria-expanded={open}
          icon={<SlidersHorizontal aria-hidden="true" size={14} />}
          onClick={() => setOpen((v) => !v)}
        >
          進階搜尋
        </Button>
        {hasFilters(value) ? <span className="text-xs text-[var(--accent-hover)]">已套用條件</span> : null}
      </div>

      {saved.length > 0 ? (
        <nav aria-label="搜尋捷徑" className="flex flex-wrap gap-1">
          {saved.map((row) => (
            <span
              key={row.id}
              className="inline-flex items-center rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]"
            >
              <button
                type="button"
                onClick={() => onReplay(row.params ?? {})}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-[var(--text-2)] hover:text-[var(--text)]"
              >
                <Bookmark aria-hidden="true" size={12} />
                {row.name}
              </button>
              <button
                type="button"
                aria-label={`刪除捷徑 ${row.name}`}
                onClick={() => void remove(row)}
                className="px-1 py-1 text-[var(--text-3)] hover:text-[var(--danger)]"
              >
                <X aria-hidden="true" size={12} />
              </button>
            </span>
          ))}
        </nav>
      ) : null}

      {open ? (
        <form
          aria-label="進階搜尋"
          onSubmit={apply}
          className="flex flex-col gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface-2)] p-3"
        >
          <div className="grid grid-cols-2 gap-2">
            <Input label="日期從" type="date" value={draft.date_from ?? ""} onChange={(e) => set({ date_from: e.target.value || undefined })} />
            <Input label="日期到" type="date" value={draft.date_to ?? ""} onChange={(e) => set({ date_to: e.target.value || undefined })} />
          </div>
          <Input label="寄件人" value={draft.from ?? ""} onChange={(e) => set({ from: e.target.value || undefined })} />
          <Input label="收件人" value={draft.to ?? ""} onChange={(e) => set({ to: e.target.value || undefined })} />
          <div className="grid grid-cols-2 gap-2">
            <Select
              label="有沒有附件"
              value={attachmentChoice}
              onChange={(e) => set({ has_attachments: e.target.value === "" ? undefined : e.target.value === "yes" })}
              options={[
                { value: "", label: "不限" },
                { value: "yes", label: "有附件" },
                { value: "no", label: "沒有附件" },
              ]}
            />
            <Input
              label="附件檔名"
              value={draft.attachment ?? ""}
              onChange={(e) => set({ attachment: e.target.value || undefined })}
            />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[var(--text-2)]">
            <label className="inline-flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={Boolean(draft.all_folders)}
                onChange={(e) => set({ all_folders: e.target.checked || undefined })}
                className="h-4 w-4 accent-[var(--accent)]"
              />
              全部資料夾
            </label>
            <label className="inline-flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={Boolean(draft.flagged)}
                onChange={(e) => set({ flagged: e.target.checked || undefined })}
                className="h-4 w-4 accent-[var(--accent)]"
              />
              只看星號
            </label>
            <label className="inline-flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={Boolean(draft.unread)}
                onChange={(e) => set({ unread: e.target.checked || undefined })}
                className="h-4 w-4 accent-[var(--accent)]"
              />
              只看未讀
            </label>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setDraft({});
                onApply({});
              }}
            >
              清除條件
            </Button>
            <Button size="sm" type="submit" variant="primary">
              套用
            </Button>
          </div>
          <div className="flex flex-wrap items-end gap-2 border-t border-[var(--border)] pt-2">
            <div className="min-w-0 flex-1">
              <Input label="捷徑名稱" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
            </div>
            <Button size="sm" loading={saving} disabled={!name.trim()} icon={<Bookmark aria-hidden="true" size={14} />} onClick={() => void save()}>
              存成捷徑
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
